// resources/js/modules/phone.js

import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import "intl-tel-input/styles";


/**
 * Initialize phone inputs.
 *
 * @param {string} selector
 * @returns {Array}
 */
export function phoneInputs(selector = "[data-phone-input]") {
    const instances = [];

    document.querySelectorAll(selector).forEach((input) => {
        const form = input.closest("form");

        const wrapper =
            input.closest("[data-phone-wrapper]");

        if (!form || !wrapper) {
            console.warn(
                "Phone input must be inside a form and data-phone-wrapper.",
                input
            );

            return;
        }


        const hiddenNumber =
            wrapper.querySelector(
                "[data-phone-number]"
            );

        const hiddenCountry =
            wrapper.querySelector(
                "[data-phone-country]"
            );

        const hiddenDialCode =
            wrapper.querySelector(
                "[data-phone-dial-code]"
            );

        const feedback =
            wrapper.querySelector(
                ".invalid-feedback"
            );


        const initialCountry =
            input.dataset.initialCountry
                ?.trim()
                .toLowerCase() ||
            "us";


        /*
        |--------------------------------------------------------------------------
        | intl-tel-input
        |--------------------------------------------------------------------------
        */

        const iti = intlTelInput(input, {
            initialCountry,

            separateDialCode: true,

            numberDisplayFormat:
                "NATIONAL",

            placeholderNumberPolicy:
                "OFF",

            formatAsYouType: true,

            strictMode: true,
        });


        /*
        |--------------------------------------------------------------------------
        | Validation messages
        |--------------------------------------------------------------------------
        | Each message can be overridden per-form via a data-msg-* attribute on
        | the phone input (e.g. data-msg-required), so pages can pull their text
        | from the app's language files. Forms that don't set them keep these
        | English defaults.
        */

        const requiredMessage =
            input.dataset.msgRequired ||
            "Phone number is required.";

        const errorMessages = {
            INVALID_COUNTRY_CODE:
                input.dataset
                    .msgInvalidCountryCode ||
                "The country code is invalid.",

            TOO_SHORT:
                input.dataset.msgTooShort ||
                "The phone number is too short.",

            TOO_LONG:
                input.dataset.msgTooLong ||
                "The phone number is too long.",

            INVALID_LENGTH:
                input.dataset
                    .msgInvalidLength ||
                "The phone number has an invalid length.",

            IS_POSSIBLE_LOCAL_ONLY:
                input.dataset.msgLocalOnly ||
                "Please include the complete phone number.",

            INVALID:
                input.dataset.msgInvalid ||
                "Please enter a valid phone number.",
        };


        /*
        |--------------------------------------------------------------------------
        | Hidden fields
        |--------------------------------------------------------------------------
        */

        function clearNumberField() {
            if (hiddenNumber) {
                hiddenNumber.value = "";
            }
        }


        function populateCountryFields() {
            const country =
                iti.getSelectedCountry();

            if (hiddenCountry) {
                hiddenCountry.value =
                    country?.iso2
                        ?.toUpperCase() ||
                    "";
            }

            if (hiddenDialCode) {
                hiddenDialCode.value =
                    country?.dialCode
                        ? `+${country.dialCode}`
                        : "";
            }
        }


        function populateNumberField() {
            if (hiddenNumber) {
                hiddenNumber.value =
                    iti.getNumber() || "";
            }
        }


        function clearHiddenFields() {
            clearNumberField();

            if (hiddenCountry) {
                hiddenCountry.value = "";
            }

            if (hiddenDialCode) {
                hiddenDialCode.value = "";
            }
        }


        /*
        |--------------------------------------------------------------------------
        | Validation state
        |--------------------------------------------------------------------------
        */

        function setValidationState(
            state = null
        ) {
            input.classList.remove(
                "is-valid",
                "is-invalid"
            );

            if (feedback) {
                feedback.classList.toggle(
                    "d-block",
                    state === false
                );
            }

            if (state === true) {
                input.classList.add(
                    "is-valid"
                );
            }

            if (state === false) {
                input.classList.add(
                    "is-invalid"
                );
            }
        }


        /*
        |--------------------------------------------------------------------------
        | Validate phone
        |--------------------------------------------------------------------------
        */

        function validatePhone(
            showFeedback = true
        ) {
            const hasValue =
                input.value.trim() !== "";


            if (
                !hasValue &&
                !input.required
            ) {
                clearHiddenFields();

                setValidationState(null);

                return true;
            }


            const isValid =
                hasValue &&
                iti.isValidNumber();


            if (isValid) {
                populateCountryFields();
                populateNumberField();

                if (showFeedback) {
                    setValidationState(
                        true
                    );
                }

                return true;
            }


            clearNumberField();

            /*
             * Keep country fields intact.
             * The country can still be valid
             * even if the phone number isn't.
             */
            populateCountryFields();


            if (showFeedback) {
                const errorCode =
                    iti.getValidationError();

                if (feedback) {
                    feedback.textContent =
                        hasValue
                            ? errorMessages[
                                  errorCode
                              ] ??
                              errorMessages.INVALID
                            : requiredMessage;
                }

                setValidationState(
                    false
                );
            }


            return false;
        }


        /*
        |--------------------------------------------------------------------------
        | Public API
        |--------------------------------------------------------------------------
        */

        input.phoneApi = {
            setCountry(countryCode) {
                if (!countryCode) {
                    return;
                }

                iti.setSelectedCountry(
                    countryCode
                        .trim()
                        .toLowerCase()
                );
            },


            getCountry() {
                return iti
                    .getSelectedCountry();
            },


            getNumber() {
                return iti.getNumber();
            },


            validate() {
                return validatePhone(
                    true
                );
            },
        };


        /*
        |--------------------------------------------------------------------------
        | Initial hidden country values
        |--------------------------------------------------------------------------
        */

        populateCountryFields();


        /*
        |--------------------------------------------------------------------------
        | Reveal server-rendered errors
        |--------------------------------------------------------------------------
        | intl-tel-input wraps the input in its own container, which breaks the
        | CSS sibling selector Bootstrap normally uses to show .invalid-feedback.
        | If the server already flagged this field invalid on page load, show
        | the feedback box explicitly.
        */

        if (
            input.classList.contains(
                "is-invalid"
            ) &&
            feedback
        ) {
            feedback.classList.add(
                "d-block"
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Validate on submit
        |--------------------------------------------------------------------------
        | Blur/countrychange won't fire if the user never focuses the field, and
        | the browser's native required check doesn't catch an invalid format.
        */

        form.addEventListener(
            "submit",
            () => {
                validatePhone(true);
            }
        );


        /*
        |--------------------------------------------------------------------------
        | Input changed
        |--------------------------------------------------------------------------
        */

        input.addEventListener(
            "input",
            () => {
                setValidationState(null);

                clearNumberField();

                populateCountryFields();
            }
        );


        /*
        |--------------------------------------------------------------------------
        | Input blur
        |--------------------------------------------------------------------------
        */

        input.addEventListener(
            "blur",
            () => {
                validatePhone(true);
            }
        );


        /*
        |--------------------------------------------------------------------------
        | Phone country changed
        |--------------------------------------------------------------------------
        */

        input.addEventListener(
            "countrychange",
            () => {
                setValidationState(null);

                clearNumberField();

                populateCountryFields();


                if (
                    input.value.trim() !==
                    ""
                ) {
                    validatePhone(true);
                }
            }
        );


        /*
        |--------------------------------------------------------------------------
        | Save instance
        |--------------------------------------------------------------------------
        */

        instances.push({
            input,
            form,
            iti,
            validate:
                validatePhone,
        });
    });

    return instances;
}